/* eslint-disable @typescript-eslint/no-explicit-any */
import { orderBy } from 'lodash-es';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { Button, Col, Flex, FormContainer, Icon, TextField } from '../index';
import { useDatatableContext, useGlobalConfiguration } from './context';
import getFilterComponent from './filters/index';
import { ColSpanDefinition, IFilterForm, RowModel } from './types';
import {
  formatDefaultValues,
  getRowIdentifier,
  isEqualCondition,
} from './utils';

type Props = {
  queryColSpan?: ColSpanDefinition;
};

const DatatableFilter = <
  TDataItem extends RowModel,
  TKeys extends string = string,
>({
  children,
  queryColSpan,
}: React.PropsWithChildren<Props>) => {
  const datatableContext = useDatatableContext<TDataItem>();
  const globalConfig = useGlobalConfiguration<TKeys>();
  const filterableColumns = datatableContext.columns.filter(
    (column) => column.filterable,
  );
  /**
   * Pristine form values, the ones the reset button brings the form back to.
   */
  const [resetValues] = React.useState(() =>
    formatDefaultValues(
      filterableColumns,
      datatableContext.defaults?.criteria?.conditions,
    ),
  );
  // The hook seeds `formValues` with the defaults and layers the url on top,
  // so it is already the right starting point for the form.
  const [defaultValues] = React.useState(
    () => datatableContext.internals.state.formValues,
  );

  const methods = useForm<IFilterForm>({
    defaultValues: defaultValues,
  });

  return (
    <div>
      <FormContainer
        methods={methods}
        submitHandler={(formData) => {
          datatableContext.internals.setter(
            datatableContext.internals.preSubmit(formData),
          );
        }}
      >
        <div className='grid gap-1'>
          <Col
            bsPrefix='g-col'
            xs={queryColSpan?.xs || 6}
            sm={queryColSpan?.sm || 6}
            md={queryColSpan?.md || 6}
            lg={queryColSpan?.lg || 4}
            xl={queryColSpan?.xl || 3}
            xxl={queryColSpan?.xxl || 2}
          >
            <TextField
              translate={(key) => key}
              label={datatableContext.translations?.searchLabel}
              placeholder={
                datatableContext.translations?.searchPlaceholder === undefined
                  ? 'Type anything ...'
                  : (datatableContext.translations?.searchPlaceholder ?? '')
              }
              type='search'
              name='searchQuery'
              size='sm'
            />
          </Col>
          {orderBy(filterableColumns, 'filter.rank').map((filterableColumn) => {
            if (!filterableColumn.filterable) {
              return undefined;
            }
            const defaultValue =
              datatableContext.defaults?.criteria?.conditions?.find(
                (condition) => isEqualCondition(condition, filterableColumn),
              )?.value;
            const isMulti =
              (filterableColumn.filterable &&
                'isMulti' in filterableColumn.filter &&
                filterableColumn.filter.isMulti) ??
              false;

            const Component =
              getFilterComponent()[filterableColumn.filter.type];
            const commonProps = {
              ...filterableColumn.filter,
              name: getRowIdentifier(filterableColumn),
              label:
                filterableColumn.filter.label ?? filterableColumn.headerName,
              defaultValue: defaultValue,
            };
            const customProps =
              filterableColumn.filter.type === 'LIST' ||
              filterableColumn.filter.type === 'TYPEAHEAD'
                ? { isMulti, isClearable: true }
                : {};
            return (
              <Col
                bsPrefix='g-col'
                key={filterableColumn.field}
                xs={filterableColumn.filterColSpan?.xs || 6}
                sm={filterableColumn.filterColSpan?.sm || 6}
                md={filterableColumn.filterColSpan?.md || 6}
                lg={filterableColumn.filterColSpan?.lg || 4}
                xl={filterableColumn.filterColSpan?.xl || 2}
                xxl={filterableColumn.filterColSpan?.xxl || 2}
              >
                <Component
                  {...commonProps}
                  {...customProps}
                  // @ts-expect-error some component have translate prop and some have Trans
                  translate={globalConfig.translate as unknown as any}
                  Trans={globalConfig.Trans as unknown as any}
                />
              </Col>
            );
          })}
          {children}
          {filterableColumns.length > 0 && (
            <Col bsPrefix='g-col' xl={3} lg={4}>
              <div className='mb-2 position-relative'>
                <label className='form-label opacity-0' htmlFor='actions'>
                  {/* Fake text to align with other fields */}
                  {'x'}
                </label>
                <Flex alignItems='center' className='space-x-2 h-100'>
                  <Button
                    id='submit-filters'
                    variant='primary'
                    size='sm'
                    type='submit'
                    className='px-3 space-x-2'
                  >
                    <Icon icon='search' size='xs' />
                    <span>
                      {datatableContext.translations?.searchBtn ?? 'Rechercher'}
                    </span>
                  </Button>
                  <Button
                    id='reset-filters'
                    variant='light'
                    type='button'
                    size='sm'
                    onClick={() => {
                      // `keepFieldsRef` makes RHF walk every mounted field and
                      // `setValue` it instead, and `setValue(name, undefined)` writes '' to an input.
                      // it used for uncontrolled fields
                      methods.reset(resetValues, { keepFieldsRef: true });
                      datatableContext.internals.setter(
                        datatableContext.internals.preSubmit(resetValues),
                      );
                    }}
                    className='px-3 space-x-2'
                  >
                    <Icon icon='reload' size='xs' />
                    <span>
                      {datatableContext.translations?.resetBtn ??
                        'Réinitialiser'}
                    </span>
                  </Button>
                </Flex>
              </div>
            </Col>
          )}
        </div>
      </FormContainer>
    </div>
  );
};
export default DatatableFilter;
